import './cardGenerationNotification.scss';
import type { HangerCard } from '../../context/MannequinContext';
import {
    loadCardGenerationNotificationsEnabled,
    saveCardGenerationNotificationsEnabled,
} from './cardGenerationNotificationStore';

const CARD_GENERATED_TOAST_TIMER_MS = 4000;
const TOAST_EXIT_MS = 220;

let notificationsEnabled = true;
let notificationPreferenceLoaded = false;
let notificationPreferenceLoadPromise: Promise<void> | null = null;

interface ShowHangerCardGeneratedToastOptions {
    cardId: string;
    cardLabel: string;
    titleText: string;
    hintText: string;
    imageUrl?: string;
}

interface ToastInstance {
    id: string;
    cardId: string;
    root: HTMLElement;
    progressBar: HTMLElement;
    closeTimerId: ReturnType<typeof setTimeout> | null;
    progressFrameId: number;
    startedAt: number;
    remainingMs: number;
    isPaused: boolean;
    isActive: boolean;
}

let stackContainer: HTMLElement | null = null;
const toastInstances: ToastInstance[] = [];
let toastIdCounter = 0;

const escapeHtml = (value: string): string =>
    value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');

const ensureStackContainer = (): HTMLElement => {
    if (!stackContainer) {
        stackContainer = document.createElement('div');
        stackContainer.className = 'hanger-card-generated-toast-stack';
        document.body.appendChild(stackContainer);
    }

    return stackContainer;
};

const clearToastTimers = (toast: ToastInstance): void => {
    if (toast.closeTimerId) {
        clearTimeout(toast.closeTimerId);
        toast.closeTimerId = null;
    }

    cancelAnimationFrame(toast.progressFrameId);
    toast.progressFrameId = 0;
};

const updateToastProgress = (toast: ToastInstance): void => {
    if (!toast.isActive || toast.isPaused) {
        return;
    }

    const elapsed = Date.now() - toast.startedAt;
    const progress = Math.min(1, elapsed / CARD_GENERATED_TOAST_TIMER_MS);
    toast.progressBar.style.transform = `scaleX(${1 - progress})`;

    if (progress < 1) {
        toast.progressFrameId = requestAnimationFrame(() => updateToastProgress(toast));
    }
};

const scheduleToastClose = (toast: ToastInstance, delayMs: number): void => {
    clearToastTimers(toast);
    toast.closeTimerId = globalThis.setTimeout(() => {
        dismissToast(toast.id);
    }, delayMs);
};

const pauseToast = (toast: ToastInstance): void => {
    if (!toast.isActive || toast.isPaused) {
        return;
    }

    toast.isPaused = true;
    clearToastTimers(toast);

    const elapsed = Date.now() - toast.startedAt;
    toast.remainingMs = Math.max(0, CARD_GENERATED_TOAST_TIMER_MS - elapsed);
};

const resumeToast = (toast: ToastInstance): void => {
    if (!toast.isActive || !toast.isPaused) {
        return;
    }

    toast.isPaused = false;
    toast.startedAt = Date.now() - (CARD_GENERATED_TOAST_TIMER_MS - toast.remainingMs);
    scheduleToastClose(toast, toast.remainingMs);
    toast.progressFrameId = requestAnimationFrame(() => updateToastProgress(toast));
};

const activateToast = (toast: ToastInstance): void => {
    toast.isActive = true;
    toast.isPaused = false;
    toast.startedAt = Date.now();
    toast.remainingMs = CARD_GENERATED_TOAST_TIMER_MS;
    toast.progressBar.style.transform = 'scaleX(1)';
    toast.root.classList.remove('hanger-card-generated-toast--waiting');

    scheduleToastClose(toast, CARD_GENERATED_TOAST_TIMER_MS);
    toast.progressFrameId = requestAnimationFrame(() => updateToastProgress(toast));
};

const removeToastElement = (toast: ToastInstance): void => {
    clearToastTimers(toast);
    toast.root.remove();
};

const cleanupStackContainer = (): void => {
    if (toastInstances.length === 0 && stackContainer) {
        stackContainer.remove();
        stackContainer = null;
    }
};

const dismissToast = (toastId: string, options?: { focusCard?: boolean }): void => {
    const toastIndex = toastInstances.findIndex((item) => item.id === toastId);
    if (toastIndex === -1) {
        return;
    }

    const toast = toastInstances[toastIndex];
    const wasActive = toast.isActive;

    if (options?.focusCard) {
        focusHangerCard(toast.cardId);
    }

    clearToastTimers(toast);
    toast.root.classList.remove('hanger-card-generated-toast--visible');
    toast.root.classList.add('hanger-card-generated-toast--leaving');

    globalThis.setTimeout(() => {
        removeToastElement(toast);
        toastInstances.splice(toastIndex, 1);
        cleanupStackContainer();

        if (wasActive && toastInstances.length > 0) {
            activateToast(toastInstances[0]);
        }
    }, TOAST_EXIT_MS);
};

const buildToastElement = (
    options: ShowHangerCardGeneratedToastOptions,
    isDarkMode: boolean
): { root: HTMLElement; progressBar: HTMLElement; closeButton: HTMLButtonElement } | null => {
    const safeTitle = escapeHtml(options.titleText);
    const safeCardLabel = escapeHtml(options.cardLabel);
    const safeHint = escapeHtml(options.hintText);
    const imageUrl = options.imageUrl?.trim();
    const hasImage = Boolean(imageUrl);
    const safeImageUrl = hasImage ? escapeHtml(imageUrl!) : '';
    const safeImageAlt = escapeHtml(options.cardLabel);
    const previewMarkup = hasImage
        ? `<div class="hanger-card-generated-toast__preview">
                <img src="${safeImageUrl}" alt="${safeImageAlt}" loading="lazy" />
           </div>`
        : '';

    const root = document.createElement('div');
    root.className = `hanger-card-generated-toast hanger-card-generated-toast--waiting${
        hasImage ? ' hanger-card-generated-toast--with-image' : ''
    }${isDarkMode ? ' hanger-card-generated-toast--dark' : ''}`;
    root.setAttribute('role', 'status');
    root.innerHTML = `
        <button type="button" class="hanger-card-generated-toast__close" aria-label="Close">&times;</button>
        <div class="hanger-card-generated-toast__content">
            <div class="hanger-card-generated-toast__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                    <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"></path>
                </svg>
            </div>
            <div class="hanger-card-generated-toast__text">
                <div class="hanger-card-generated-toast__title">${safeTitle}</div>
                <div class="hanger-card-generated-toast__card-name">${safeCardLabel}</div>
                <div class="hanger-card-generated-toast__hint">${safeHint}</div>
            </div>
            ${previewMarkup}
        </div>
        <div class="hanger-card-generated-toast__progress">
            <div class="hanger-card-generated-toast__progress-bar"></div>
        </div>
    `;

    const progressBar = root.querySelector('.hanger-card-generated-toast__progress-bar');
    const closeButton = root.querySelector('.hanger-card-generated-toast__close');

    if (!(progressBar instanceof HTMLElement) || !(closeButton instanceof HTMLButtonElement)) {
        root.remove();
        return null;
    }

    return { root, progressBar, closeButton };
};

export const getHangerCardDisplayName = (
    card: HangerCard,
    cardIndex: number,
    translate: (key: string, options?: Record<string, unknown>) => string
): string => {
    const cardTitle = translate('mannequin.aiGeneration.hangerFlow.uploadGenerate.cardTitle', {
        number: cardIndex + 1,
    });
    const barcode = card.config.barcode?.trim();

    return barcode ? `${cardTitle} (${barcode})` : cardTitle;
};

export const getHangerCardGeneratedImageUrl = (card: HangerCard): string | undefined => {
    for (const result of card.generatedResults) {
        const generatedUrl = result.generatedUrls.find((url) => url?.trim());
        if (generatedUrl) {
            return generatedUrl.trim();
        }
    }

    return undefined;
};

export const focusHangerCard = (cardId: string): void => {
    const cardElement = document.querySelector(`[data-hanger-card-id="${cardId}"]`);
    if (!cardElement) {
        return;
    }

    const scrollTarget =
        cardElement.querySelector('[data-hanger-card-generated-image]') ?? cardElement;

    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    cardElement.classList.add('hanger-card--focused');

    globalThis.setTimeout(() => {
        cardElement.classList.remove('hanger-card--focused');
    }, 2200);
};

export const ensureCardGenerationNotificationPreferenceLoaded = (): Promise<void> => {
    if (notificationPreferenceLoaded) {
        return Promise.resolve();
    }

    if (!notificationPreferenceLoadPromise) {
        notificationPreferenceLoadPromise = loadCardGenerationNotificationsEnabled().then((enabled) => {
            notificationsEnabled = enabled;
            notificationPreferenceLoaded = true;
        });
    }

    return notificationPreferenceLoadPromise;
};

export const isCardGenerationNotificationsEnabled = (): boolean => notificationsEnabled;

export const setCardGenerationNotificationsEnabled = async (enabled: boolean): Promise<void> => {
    notificationsEnabled = enabled;
    notificationPreferenceLoaded = true;
    await saveCardGenerationNotificationsEnabled(enabled);
};

export const toggleCardGenerationNotificationsEnabled = async (): Promise<boolean> => {
    await ensureCardGenerationNotificationPreferenceLoaded();
    const nextEnabled = !notificationsEnabled;
    await setCardGenerationNotificationsEnabled(nextEnabled);
    return nextEnabled;
};

export const showHangerCardGeneratedToast = (options: ShowHangerCardGeneratedToastOptions): void => {
    if (!notificationsEnabled) {
        return;
    }
    const container = ensureStackContainer();
    const isDarkMode = Boolean(document.querySelector('.darkMode'));
    const builtToast = buildToastElement(options, isDarkMode);

    if (!builtToast) {
        return;
    }

    const { root, progressBar, closeButton } = builtToast;
    const toastId = `hanger-card-generated-toast-${++toastIdCounter}`;

    const toast: ToastInstance = {
        id: toastId,
        cardId: options.cardId,
        root,
        progressBar,
        closeTimerId: null,
        progressFrameId: 0,
        startedAt: 0,
        remainingMs: CARD_GENERATED_TOAST_TIMER_MS,
        isPaused: false,
        isActive: false,
    };

    container.appendChild(root);
    requestAnimationFrame(() => {
        root.classList.add('hanger-card-generated-toast--visible');
    });

    closeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        dismissToast(toastId);
    });

    root.addEventListener('mouseenter', () => pauseToast(toast));
    root.addEventListener('mouseleave', () => resumeToast(toast));

    root.addEventListener('click', (event) => {
        if ((event.target as HTMLElement).closest('.hanger-card-generated-toast__close')) {
            return;
        }

        focusHangerCard(toast.cardId);

        if (toast.isActive) {
            dismissToast(toastId);
        }
    });

    toastInstances.push(toast);

    if (toastInstances.length === 1) {
        activateToast(toast);
    }
};

void ensureCardGenerationNotificationPreferenceLoaded();
